import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

declare global {
    interface Window {
        appendHelcimPayIframe?: (checkoutToken: string) => void;
    }
}
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Collapse,
    IconButton,
    Snackbar
} from '@mui/material';
import {
    CreditCard as CreditCardIcon,
    Add as AddIcon,
    GetApp as ExportIcon,
    Receipt as ReceiptIcon,
    Delete as DeleteIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { transactionAPI, paymentMethodAPI, memberGroupAPI, tenantSettingsAPI } from '../../services/api';
import { Transaction, MemberGroup, TenantSettings } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import { useRequiredMemberContext } from '../../hooks/useMemberContext';
import GroupPaymentConfirmDialog from '../../components/billing/GroupPaymentConfirmDialog';
import { generateInvoiceHTML, openInvoiceWindow } from '../../utils/invoiceGenerator';

interface PaymentMethod {
    id: string;
    type: 'credit_card' | 'debit_card';
    lastFour: string;
    brand: string;
    expiryMonth: number;
    expiryYear: number;
    cardHolderName?: string;
    isDefault: boolean;
}

const BillingPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, memberProfile, isReady } = useRequiredMemberContext();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [helcimScriptLoaded, setHelcimScriptLoaded] = useState(false);
    const [memberGroup, setMemberGroup] = useState<MemberGroup | null>(null);
    const [groupMembers, setGroupMembers] = useState<any[]>([]);
    // Removed: showGroupPaymentDialog and pendingPaymentInfo - payment is now auto-resolved from primary contact
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<string | null>(null);
    const [groupMessageExpanded, setGroupMessageExpanded] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [transactionDetailsOpen, setTransactionDetailsOpen] = useState(false);
    const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
    const [retryPaymentDialogOpen, setRetryPaymentDialogOpen] = useState<boolean>(false);
    const [retryPaymentProcessing, setRetryPaymentProcessing] = useState<boolean>(false);
    const [retryPaymentResults, setRetryPaymentResults] = useState<Array<{
        transactionId: string;
        success: boolean;
        message: string;
        type?: string;
        amount?: number;
    }>>([]);
    const [failedTransactionsCount, setFailedTransactionsCount] = useState<number>(0);
    const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
    const secretTokenRef = React.useRef<string>('');
    const checkoutTokenRef = React.useRef<string>('');
    const helcimObserverRef = React.useRef<MutationObserver | null>(null);
    const helcimIntervalRef = React.useRef<number | null>(null);
    const helcimIframeRef = React.useRef<HTMLIFrameElement | null>(null);
    // Payment method form state
    const [paymentForm, setPaymentForm] = useState({
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        cardholderName: '',
        billingAddress: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'CA'
        }
    });

    const fetchTenantSettings = async () => {
        try {
            const response = await tenantSettingsAPI.getAllSettings();
            setTenantSettings(response.data);
        } catch (err) {
            console.error('Error fetching tenant settings:', err);
            // Non-critical error, just log it
        }
    };

    const fetchTransactions = async () => {
        if (!memberProfile?.id) return;

        setLoading(true);
        try {
            // Get transactions for the last year only
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            // Always fetch user's own transactions (what they're being billed for)
            // The backend will return transactions where billedToMemberId = user's id
            // OR where billedToGroupId = user's group (if they're in a group with group billing)
            const response = await transactionAPI.getMyTransactions({
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                startDate: oneYearAgo.toISOString(),
            });

            setTransactions(response.data.transactions);
            setTotalTransactions(response.data.total);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setError('Failed to fetch billing history');
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const response = await paymentMethodAPI.getMyPaymentMethods();
            setPaymentMethods(response.data.paymentMethods || []);
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setPaymentMethods([]);
        }
    };

    const fetchMemberGroup = async () => {
        if (!memberProfile?.memberGroupId) {
            setMemberGroup(null);
            return;
        }

        try {
            const groupResponse = await memberGroupAPI.getById(memberProfile.memberGroupId);
            setMemberGroup(groupResponse.data);

            // If user is primary contact, fetch group members for dialog
            if (groupResponse.data.primaryContactId === user?.id) {
                const membersResponse = await memberGroupAPI.getMembers(memberProfile.memberGroupId);
                setGroupMembers(membersResponse.data);
            }
        } catch (error) {
            console.error('Error fetching member group:', error);
        }
    };

    const fetchPendingTransactions = async () => {
        if (!memberProfile?.id) return;

        try {
            const response = await transactionAPI.getMyTransactions({
                status: 'pending',
                limit: 100,
            });
            setPendingTransactions(response.data.transactions);
            
            // Also count failed/pending transactions for retry functionality
            const allTransactions = await transactionAPI.getMyTransactions({
                limit: 1000,
            });
            const failedCount = allTransactions.data.transactions.filter(
                (tx: Transaction) => tx.status === 'failed' || tx.status === 'pending'
            ).length;
            setFailedTransactionsCount(failedCount);
        } catch (error) {
            console.error('Error fetching pending transactions:', error);
        }
    };

    const calculateNextPayment = () => {
        if (pendingTransactions.length === 0) return null;

        // Find the earliest transaction date
        const sortedTransactions = [...pendingTransactions]
            .filter(t => t.transactionDate)
            .sort((a, b) => new Date(a.transactionDate!).getTime() - new Date(b.transactionDate!).getTime());

        if (sortedTransactions.length === 0) return null;

        const earliestDate = sortedTransactions[0].transactionDate;
        
        // Sum all transactions due on that date
        const totalAmount = pendingTransactions
            .filter(t => t.transactionDate && formatDate(t.transactionDate) === formatDate(earliestDate!))
            .reduce((sum, t) => sum + Number(t.totalAmount || t.amount), 0);

        return {
            date: earliestDate,
            amount: totalAmount
        };
    };

    useEffect(() => {
        fetchPaymentMethods();
        fetchMemberGroup();
        fetchPendingTransactions();
        fetchTenantSettings();
    }, [isReady, memberProfile]);

    useEffect(() => {
        // Fetch transactions when memberGroup changes or pagination changes
        fetchTransactions();
    }, [isReady, memberProfile, memberGroup, page, rowsPerPage]);

    useEffect(() => {
        // Dynamically load HelcimPay.js start script used by the test.html example
        const script = document.createElement('script');
        script.src = 'https://secure.helcim.app/helcim-pay/services/start.js';
        script.async = true;
        script.onload = () => {
            setHelcimScriptLoaded(true);
            // If the library exposes HelcimPay immediately, initialize with default config
            if ((window as any).HelcimPay) {
                try {
                    (window as any).HelcimPay.init({});
                } catch (err) {
                    // Non-fatal - the library may not need init or may be initialized later
                    // eslint-disable-next-line no-console
                    console.warn('HelcimPay init failed or not required', err);
                }
            }
        };
        script.onerror = () => {
            console.error('Failed to load HelcimPay script');
            setError('Payment system failed to load. Please refresh the page.');
        };
        document.body.appendChild(script);

        return () => {
            // cleanup script when component unmounts
            if (document.body.contains(script)) document.body.removeChild(script);
            setHelcimScriptLoaded(false);
        };
    }, []);

    // Cleanup watcher on unmount
    useEffect(() => {
        return () => {
            disconnectWatcher();
        };
    }, []);

    const handleAddPaymentMethod = () => {
        initializeHelcimPay();
    };

    const initializeHelcimPay = async () => {

        const response = await paymentMethodAPI.initializePaymentProcessor();
        // ** DEBUGGING ** 
        // console.log('Payment processor initialized:', response.data);

        if (response?.data) {
            secretTokenRef.current = response.data.secretToken || '';
            checkoutTokenRef.current = response.data.checkoutToken || '';
        }

        setIsSubmitting(true);

        // ** DEBUGGING ** 
        // console.log('Starting HelcimPay initialization...');
        // console.log('Initializing HelcimPay with token:', secretTokenRef.current);

        // Configure HelcimPay for card verification
        const helcimPayConfig = {
            token: secretTokenRef.current || '',
            amount: 0,
            currency: 'CAD',
            transactionType: 'verify' as const,
            customer: {
                // customerCode: `MP-${memberProfile?.id}`,
                contactName: `${memberProfile?.firstName} ${memberProfile?.lastName}`,
                emailAddress: memberProfile?.email,
                phoneNumber: memberProfile?.phone,
                cellPhoneNumber: memberProfile?.mobile
            }
        };

        try {
            const win = window as any;

            // Prefer the helper that appends the iframe if available (as in test.html example)
            if (typeof win.appendHelcimPayIframe === 'function' && checkoutTokenRef.current) {
                try {
                    win.appendHelcimPayIframe(checkoutTokenRef.current);
                    // start watching for the iframe to be closed/removed so we can reset the UI
                    startHelcimCloseWatcher();
                    return;
                } catch (err) {
                    console.error('appendHelcimPayIframe failed', err);
                }
            }

            // If the HelcimPay object is available and exposes render/open, use it
            if (win.HelcimPay && typeof win.HelcimPay.render === 'function') {
                win.HelcimPay.render(helcimPayConfig);
                if (typeof win.HelcimPay.open === 'function') {
                    win.HelcimPay.open();
                }
                // watch for close as well
                startHelcimCloseWatcher();
                return;
            }

            // If neither approach is available, set a helpful error
            console.error('HelcimPay is not initialized (no helper and no HelcimPay.render)');
            setError('Payment system is not available. Please refresh the page and try again.');
            setIsSubmitting(false);

        } catch (error) {
            console.error('Error initializing HelcimPay:', error);
            setError('Failed to initialize payment system. Please try again.');
            setIsSubmitting(false);
        }
    };

    const startHelcimCloseWatcher = () => {
        // If already watching, do nothing
        if (helcimObserverRef.current) return;

        const checkForIframe = () => {
            // find any iframe that looks like Helcim (heuristic)
            const iframes = Array.from(document.getElementsByTagName('iframe')) as HTMLIFrameElement[];
            for (const f of iframes) {
                const src = f.src || '';
                if (src.toLowerCase().includes('helcim') || src.toLowerCase().includes('helcim-pay')) {
                    helcimIframeRef.current = f;
                    return f;
                }
            }
            return null;
        };

        // Create observer to detect removal of the iframe
        const observer = new MutationObserver((mutations) => {
            // if we don't yet have the iframe element, try to find it
            if (!helcimIframeRef.current) {
                const found = checkForIframe();
                if (found) {
                    // found the iframe; keep watching for its removal
                    return;
                }
            } else {
                // if iframe was removed from DOM, reset UI
                if (!document.body.contains(helcimIframeRef.current)) {
                    setIsSubmitting(false);
                    disconnectWatcher();
                }
            }
        });

        helcimObserverRef.current = observer;
        observer.observe(document.body, { childList: true, subtree: true });

        // Start a fallback interval to check existence (helps if iframe already exists)
        const existing = checkForIframe();
        if (existing) {
            // it already exists; monitor until it's removed
            helcimIframeRef.current = existing;
        }

        helcimIntervalRef.current = window.setInterval(() => {
            const f = helcimIframeRef.current;
            if (f) {
                // removed from DOM
                if (!document.body.contains(f)) {
                    setIsSubmitting(false);
                    disconnectWatcher();
                    return;
                }
                // hidden (offsetParent null means not visible)
                try {
                    if (f.offsetParent === null) {
                        setIsSubmitting(false);
                        disconnectWatcher();
                        return;
                    }
                } catch (err) {
                    // ignore cross-origin access issues
                }
            }

            // Also check for any overlay/dialog elements removed that mention helcim in id/class
            const helcimNodes = Array.from(document.querySelectorAll('[id*="helcim" i], [class*="helcim" i]'));
            if (helcimNodes.length === 0 && helcimIframeRef.current) {
                setIsSubmitting(false);
                disconnectWatcher();
            }
        }, 500);
    };

    const disconnectWatcher = () => {
        if (helcimObserverRef.current) {
            helcimObserverRef.current.disconnect();
            helcimObserverRef.current = null;
        }
        if (helcimIntervalRef.current) {
            clearInterval(helcimIntervalRef.current);
            helcimIntervalRef.current = null;
        }
        helcimIframeRef.current = null;
    };

    const handleVerificationSuccess = async (result: any) => {
        try {
            // ** DEBUGGING **
            // console.log('Processing successful payment verification:', result);

            if (!memberProfile?.id) {
                throw new Error('Member profile ID is required');
            }

            // Parse the event message which contains the transaction details
            let transactionData = null;
            if (result.eventMessage && typeof result.eventMessage === 'string') {
                try {
                    const parsed = JSON.parse(result.eventMessage);
                    // The structure is: { data: { data: { transactionId, cardToken, etc. } } }
                    transactionData = parsed.data?.data || parsed.data || parsed;
                } catch (e) {
                    console.error('Failed to parse eventMessage:', e);
                }
            }

            if (!transactionData) {
                console.error('No transaction data found in result:', result);
                throw new Error('Invalid payment response received. Please try again.');
            }

            // Extract card data from the Helcim response
            const cardToken = transactionData.cardToken;
            const customerId = transactionData.customerCode; // Helcim uses 'customerCode'
            const cardNumber = {
                lastFour: transactionData.cardNumber?.slice(-4) || '****',
                brand: transactionData.cardType || 'Unknown',
                cardHolderName: transactionData.cardHolderName
            };

            // ** DEBUGGING **
            // console.log('Extracted data:', { 
            //     cardToken, 
            //     customerId, 
            //     cardNumber,
            //     transactionId: transactionData.transactionId 
            // });

            if (!cardToken) {
                console.error('No card token found in transaction data:', transactionData);
                throw new Error('Card token not found in payment response. Please try again.');
            }

            // Call your backend API to store the payment method using HelcimPay token
            const response = await paymentMethodAPI.addPaymentMethodToken({
                cardToken,
                customerId,
                cardNumber,
                verificationMessage: result.eventMessage || JSON.stringify(result),
                memberProfileId: memberProfile.id
            });

            // ** DEBUGGING **
            // console.log('Payment method added successfully:', response);

            // Store payment info for potential group application
            const paymentInfo = response.data.pmtProcessorInfo || {
                helcim: {
                    cards: [{
                        id: cardToken,
                        token: cardToken,
                        lastFour: cardNumber.lastFour,
                        brand: cardNumber.brand,
                        cardHolderName: cardNumber.cardHolderName,
                        customerId: customerId
                    }]
                }
            };

            // Payment info is now automatically used from primary contact's profile
            // No need to show dialog or copy payment info to group
            
            // Close HelcimPay modal if still open
            if (typeof (window as any).HelcimPay !== 'undefined' && (window as any).HelcimPay.close) {
                (window as any).HelcimPay.close();
            }

            // Refresh payment methods
            await fetchPaymentMethods();
            await fetchPendingTransactions();
            setIsSubmitting(false);

            setError(null);
            
            // Check if there are failed transactions and prompt to retry
            if (failedTransactionsCount > 0) {
                setRetryPaymentDialogOpen(true);
            }
        } catch (error: any) {
            console.error('Error storing payment method:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to store payment method.';
            setError(errorMessage);
            setIsSubmitting(false);
        }
    };

    // Listen for Helcim postMessage events identifying success/abort for a checkout token
    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            try {
                if (!event?.data) return;

                const token = checkoutTokenRef.current;
                if (!token) return; // nothing to match against yet

                const helcimPayJsIdentifierKey = `helcim-pay-js-${token}`;

                if (event.data.eventName !== helcimPayJsIdentifierKey) return;

                const status = event.data.eventStatus;
                if (status === 'ABORTED') {
                    console.error('Transaction failed!', event.data.eventMessage);
                    setError(event.data.eventMessage || 'Payment was aborted');
                    setIsSubmitting(false);
                }

                if (status === 'SUCCESS') {

                    // ** DEBUGGING **
                    // console.log('Transaction success!', event.data.eventMessage);
                    // console.log('Full event data:', JSON.stringify(event.data, null, 2));
                    
                    // Helcim sends the transaction data in event.data
                    // The card token should be in event.data or we need to fetch it from the transaction
                    await handleVerificationSuccess(event.data);
                    // close Helcim modal if possible
                    const win = window as any;
                    if (win.HelcimPay && typeof win.HelcimPay.close === 'function') {
                        try { win.HelcimPay.close(); } catch (err) { /* ignore */ }
                    }
                    setIsSubmitting(false);
                }
            } catch (err) {
                console.error('Error handling Helcim message event', err);
            }
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    const handleExportTransactions = () => {
        // Helper function to escape and quote CSV fields
        const escapeCSVField = (field: string): string => {
            // Convert to string and escape double quotes by doubling them
            const fieldStr = String(field || '');
            const escaped = fieldStr.replace(/"/g, '""');
            // Always encapsulate in double quotes
            return `"${escaped}"`;
        };

        const headers = ['Transaction Date', 'Created Date', 'Member Charged', 'Description', 'Type', 'Amount', 'Status'];

        const rows = transactions.map(transaction => {
            const memberName = transaction.memberProfile 
                ? `${transaction.memberProfile.firstName} ${transaction.memberProfile.lastName}`
                : 'N/A';
            
            return [
                formatDate(transaction.transactionDate || transaction.createdAt),
                formatDate(transaction.createdAt),
                memberName,
                transaction.description || '',
                transaction.type,
                `$${transaction.totalAmount || transaction.amount}`,
                transaction.status
            ];
        });

        // Encapsulate all headers and row fields in double-quotes
        const csvContent = [
            headers.map(escapeCSVField).join(','),
            ...rows.map(row => row.map(escapeCSVField).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `billing-history-${formatDate(new Date().toISOString())}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'failed':
            case 'cancelled':
                return 'error';
            case 'refunded':
                return 'info';
            default:
                return 'default';
        }
    };

    const formatTransactionType = (type: string) => {
        return type.split('_').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const isCardExpired = (expiryMonth: number, expiryYear: number) => {
        if (!expiryMonth || !expiryYear) return false;
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed
        
        if (expiryYear < currentYear) return true;
        if (expiryYear === currentYear && expiryMonth < currentMonth) return true;
        return false;
    };

    const handleDeleteCard = async (cardId: string) => {
        try {
            await paymentMethodAPI.deletePaymentMethod(cardId);
            setDeleteDialogOpen(false);
            setCardToDelete(null);
            await fetchPaymentMethods();
            setError(null);
        } catch (error: any) {
            console.error('Error deleting payment method:', error);
            setError(error?.response?.data?.message || 'Failed to delete payment method');
            setDeleteDialogOpen(false);
        }
    };

    const handleSetDefaultCard = async (cardId: string) => {
        try {
            await paymentMethodAPI.setDefaultPaymentMethod(cardId);
            await fetchPaymentMethods();
            setError(null);
        } catch (error: any) {
            console.error('Error setting default payment method:', error);
            setError(error?.response?.data?.message || 'Failed to set default payment method');
        }
    };

    const handleDownloadInvoice = async (transaction: Transaction, event: React.MouseEvent) => {
        event.stopPropagation();
        
        if (transaction.status !== 'completed') {
            setError('Invoice is only available for completed transactions');
            return;
        }

        try {
            const response = await paymentMethodAPI.getInvoice(transaction.id);
            const invoiceData = response.data;

            // Get member profile for address
            const member = transaction.memberProfile as any;
            const memberInfo = member ? {
                address: member.address,
                city: member.city,
                state: member.state,
                zipCode: member.zipCode,
                country: member.country
            } : undefined;

            // Generate invoice HTML using shared utility
            const invoiceHtml = generateInvoiceHTML({
                invoiceData,
                memberInfo,
                tenantSettings,
                transactionId: transaction.id
            });

            // Open invoice in new window
            if (!openInvoiceWindow(invoiceHtml)) {
                setError('Please allow popups to view the invoice');
            }
        } catch (error: any) {
            console.error('Error downloading invoice:', error);
            setError(error?.response?.data?.message || 'Failed to download invoice');
        }
    };

    const openDeleteDialog = (cardId: string) => {
        setCardToDelete(cardId);
        setDeleteDialogOpen(true);
    };

    const handleTransactionClick = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setTransactionDetailsOpen(true);
    };

    // Handle retry payment for all failed/pending transactions
    const handleRetryPayment = async () => {
        setRetryPaymentProcessing(true);
        setRetryPaymentResults([]);

        try {
            // Get all failed and pending transactions
            const allTransactions = await transactionAPI.getMyTransactions({
                limit: 1000,
            });

            const failedPendingTransactions = allTransactions.data.transactions.filter(
                (tx: Transaction) => tx.status === 'failed' || tx.status === 'pending'
            );

            if (failedPendingTransactions.length === 0) {
                setRetryPaymentResults([{
                    transactionId: 'none',
                    success: false,
                    message: 'No failed or pending transactions to retry'
                }]);
                setRetryPaymentProcessing(false);
                return;
            }

            const results: Array<{
                transactionId: string;
                success: boolean;
                message: string;
                type?: string;
                amount?: number;
            }> = [];

            // Process each transaction individually to track results
            for (const transaction of failedPendingTransactions) {
                try {
                    const response = await paymentMethodAPI.processTransaction(transaction.id);
                    
                    results.push({
                        transactionId: transaction.id,
                        success: true,
                        message: response.data.message || 'Payment processed successfully',
                        type: transaction.type,
                        amount: transaction.totalAmount || transaction.amount
                    });
                } catch (err: any) {
                    const errorMessage = err?.response?.data?.message || 'Failed to process payment';
                    
                    results.push({
                        transactionId: transaction.id,
                        success: false,
                        message: errorMessage,
                        type: transaction.type,
                        amount: transaction.totalAmount || transaction.amount
                    });
                }
            }

            setRetryPaymentResults(results);
            
            // Refresh transactions to show updated statuses
            await fetchTransactions();
            await fetchPendingTransactions();
        } catch (err: any) {
            setRetryPaymentResults([{
                transactionId: 'error',
                success: false,
                message: err?.message || 'An unexpected error occurred'
            }]);
        } finally {
            setRetryPaymentProcessing(false);
        }
    };

    // Handle close retry payment dialog
    const handleCloseRetryPaymentDialog = () => {
        setRetryPaymentDialogOpen(false);
        setRetryPaymentResults([]);
    };

    if (loading && transactions.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        // Redirect to billing page and show error as toast
        setSnackbarMessage(error);
        setError(null);
        // Don't return early - let the page render normally
    }

    if (!isReady || !memberProfile) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="info">Loading billing information...</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>

                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Billing & Payments
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Manage payment methods and view your transaction history
                </Typography>
                <div />
            </Paper>

            <Grid container spacing={3}>
                {/* Payment Method Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Payment Method</Typography>
                            </Box>

                            {/* Check for group payment first */}
                            {memberGroup?.paymentInfo && user?.id !== memberGroup.primaryContactId ? (
                                <Box>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Billing to Group: <strong>{memberGroup.groupName}</strong>
                                    </Alert>
                                    {(() => {
                                        const groupCards = memberGroup.paymentInfo?.helcim?.cards || [];
                                        const defaultCard = groupCards.find((c: any) => c.isDefault) || groupCards[0];
                                        
                                        if (defaultCard) {
                                            return (
                                                <Paper
                                                    elevation={2}
                                                    sx={{
                                                        p: 2.5,
                                                        mb: 2,
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: 'white',
                                                        borderRadius: 2
                                                    }}
                                                >
                                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                        <Box flex={1}>
                                                            {defaultCard.cardHolderName && (
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                                    {defaultCard.cardHolderName}
                                                                </Typography>
                                                            )}
                                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                                {defaultCard.brand || 'Card'}
                                                            </Typography>
                                                            <Typography variant="h6" sx={{ letterSpacing: 2, mb: 1 }}>
                                                                •••• •••• •••• {defaultCard.lastFour}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                                Group payment method
                                                            </Typography>
                                                        </Box>
                                                        <Box textAlign="right">
                                                            <CreditCardIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Box>
                            ) : paymentMethods.length > 0 ? (
                                <Box>
                                    {paymentMethods.map((method) => {
                                        const expired = isCardExpired(method.expiryMonth, method.expiryYear);
                                        return (
                                            <Paper
                                                key={method.id}
                                                elevation={2}
                                                sx={{
                                                    p: 2.5,
                                                    mb: 2,
                                                    background: expired 
                                                        ? 'linear-gradient(135deg, #757575 0%, #616161 100%)'
                                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    borderRadius: 2,
                                                    position: 'relative'
                                                }}
                                            >
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                    <Box flex={1}>
                                                        {method.cardHolderName && (
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                                {method.cardHolderName}
                                                            </Typography>
                                                        )}
                                                        {/* <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                            {method.brand} {method.type.replace('_', ' ')}
                                                        </Typography> */}
                                                        <Typography variant="h6" sx={{ letterSpacing: 2, mb: 1 }}>
                                                            •••• •••• •••• {method.lastFour}
                                                        </Typography>
                                                        <Box display="flex" gap={3}>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                                                    Expiry
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                                    {method.expiryMonth && method.expiryYear 
                                                                        ? `${String(method.expiryMonth).padStart(2, '0')}/${method.expiryYear}`
                                                                        : '••/••'
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                                                    CVV
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                                    •••
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {expired && (
                                                            <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
                                                                This card has expired
                                                            </Alert>
                                                        )}
                                                    </Box>
                                                    <Box textAlign="right">
                                                        <CreditCardIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                                        {method.isDefault && (
                                                            <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
                                                                Default
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box display="flex" gap={1} mt={2}>
                                                    {!method.isDefault && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={expired ? <StarBorderIcon /> : <StarIcon />}
                                                            onClick={() => handleSetDefaultCard(method.id)}
                                                            disabled={expired}
                                                            sx={{
                                                                color: 'white',
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                '&:hover': {
                                                                    borderColor: 'white',
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                                },
                                                                '&:disabled': {
                                                                    color: 'rgba(255, 255, 255, 0.3)',
                                                                    borderColor: 'rgba(255, 255, 255, 0.2)'
                                                                }
                                                            }}
                                                        >
                                                            Set as Default
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => openDeleteDialog(method.id)}
                                                        disabled={paymentMethods.length === 1}
                                                        sx={{
                                                            color: 'white',
                                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                                            '&:hover': {
                                                                borderColor: '#ff1744',
                                                                backgroundColor: 'rgba(255, 23, 68, 0.1)'
                                                            },
                                                            '&:disabled': {
                                                                color: 'rgba(255, 255, 255, 0.3)',
                                                                borderColor: 'rgba(255, 255, 255, 0.2)'
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddPaymentMethod}
                                        disabled={isSubmitting || !helcimScriptLoaded}
                                        fullWidth
                                    >
                                        {!helcimScriptLoaded ? 'Loading Payment System...' :
                                            isSubmitting ? 'Processing...' : 'Update Payment Method'}
                                    </Button>
                                </Box>
                            ) : (
                                <Box textAlign="center" py={3}>
                                    <CreditCardIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary" gutterBottom>
                                        No payment method on file
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddPaymentMethod}
                                        disabled={isSubmitting || !helcimScriptLoaded}
                                    >
                                        {!helcimScriptLoaded ? 'Loading Payment System...' :
                                            isSubmitting ? 'Processing...' : 'Add Payment Method'}
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Account Summary */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">Membership Summary</Typography>
                            </Box>

                            <Box py={2}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Tier
                                </Typography>
                                <Typography variant="h6" gutterBottom>
                                    {memberProfile.membershipTier?.name || 'N/A'}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Status
                                </Typography>
                                <Typography variant="h6" gutterBottom>
                                    {memberProfile.status || 'N/A'}
                                </Typography>

                                {memberProfile.monthlyPrice && (
                                    <>
                                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                            Monthly Fee
                                        </Typography>
                                        <Typography variant="h6">
                                            ${memberProfile.monthlyPrice}/month
                                        </Typography>
                                    </>
                                )}

                                {memberProfile.startDate && (
                                    <>
                                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                            Member Since
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatDate(memberProfile.startDate)}
                                        </Typography>
                                    </>
                                )}

                                {(() => {
                                    const nextPayment = calculateNextPayment();
                                    if (nextPayment) {
                                        return (
                                            <>
                                                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                                    Next Payment
                                                </Typography>
                                                <Typography variant="h6" color="primary">
                                                    ${nextPayment.amount.toFixed(2)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Due on {formatDate(nextPayment.date!)}
                                                </Typography>
                                            </>
                                        );
                                    }
                                    return null;
                                })()}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Group Billing Notice */}
                {memberGroup && memberGroup.chargeToGroupPrimaryContact && (
                    <Grid size={{ xs: 12 }}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                You are part of group <strong>{memberGroup.groupName}</strong>. 
                                All charges are billed to the group's primary contact.
                            </Typography>
                        </Alert>
                    </Grid>
                )}

                {/* Transaction History */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    Transaction History (Last 12 Months)
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<ExportIcon />}
                                    onClick={handleExportTransactions}
                                    disabled={transactions.length === 0}
                                >
                                    Export
                                </Button>
                            </Box>

                            {transactions.length > 0 ? (
                                <>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Transaction Date</TableCell>
                                                    <TableCell>Member Charged</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Type</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell align="center">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {transactions.map((transaction) => (
                                                    <TableRow 
                                                        key={transaction.id}
                                                        onClick={() => handleTransactionClick(transaction)}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            '&:hover': { backgroundColor: 'action.hover' }
                                                        }}
                                                    >
                                                        <TableCell>
                                                            {formatDate(transaction.transactionDate || transaction.createdAt)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {transaction.memberProfile 
                                                                ? `${transaction.memberProfile.firstName} ${transaction.memberProfile.lastName}`
                                                                : 'N/A'
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {transaction.description || '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatTransactionType(transaction.type)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            ${(transaction.totalAmount || transaction.amount)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={transaction.status}
                                                                color={getStatusColor(transaction.status)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {transaction.status === 'completed' && transaction.paymentGroupId && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleDownloadInvoice(transaction, e)}
                                                                    title="Download Invoice"
                                                                    sx={{
                                                                        color: 'primary.main',
                                                                        '&:hover': {
                                                                            backgroundColor: 'action.hover'
                                                                        }
                                                                    }}
                                                                >
                                                                    <DownloadIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        count={totalTransactions}
                                        page={page}
                                        onPageChange={(_, newPage) => setPage(newPage)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                    />
                                </>
                            ) : (
                                <Box textAlign="center" py={4}>
                                    <ReceiptIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        No transactions found for the past 12 months
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* HelcimPay.js Payment Form Container */}
            <div id="helcimpay-container">
                {/* The HelcimPay.js iframe will be rendered here */}
            </div>

            {/* Removed: GroupPaymentConfirmDialog - payment is now auto-resolved from primary contact */}

            {/* Transaction Details Dialog */}
            <Dialog
                open={transactionDetailsOpen}
                onClose={() => setTransactionDetailsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Transaction Details
                </DialogTitle>
                <DialogContent>
                    {selectedTransaction && (
                        <Box sx={{ pt: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Transaction Date
                                </Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.transactionDate 
                                        ? formatDate(selectedTransaction.transactionDate)
                                        : formatDate(selectedTransaction.createdAt)
                                    }
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                                    Created: {formatDate(selectedTransaction.createdAt)}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Member Charged
                                </Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.memberProfile 
                                        ? `${selectedTransaction.memberProfile.firstName} ${selectedTransaction.memberProfile.lastName}`
                                        : 'N/A'
                                    }
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Billing Context
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {(selectedTransaction as any).billedToGroupId ? (
                                        <Chip
                                            label="Group Billing"
                                            size="small"
                                            color="info"
                                            icon={<CreditCardIcon />}
                                        />
                                    ) : (
                                        <Chip
                                            label="Individual Billing"
                                            size="small"
                                            color="default"
                                        />
                                    )}
                                </Box>
                                {(selectedTransaction as any).billedToGroupId && memberGroup?.groupName && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        This charge is billed to you as the primary contact of group <strong>{memberGroup.groupName}</strong>
                                    </Typography>
                                )}
                                {!(selectedTransaction as any).billedToGroupId && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        This charge is billed directly to the member
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Description
                                </Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.description || 'No description'}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Type
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={formatTransactionType(selectedTransaction.type)}
                                        size="small"
                                        color="default"
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Status
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={selectedTransaction.status}
                                        size="small"
                                        color={getStatusColor(selectedTransaction.status)}
                                    />
                                </Box>
                                {selectedTransaction.status === 'failed' && selectedTransaction.metadata?.lastError && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Failure Reason:
                                        </Typography>
                                        <Typography variant="body2">
                                            {selectedTransaction.metadata.lastError}
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>

                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">Amount</Typography>
                                    <Typography variant="body2">${Number(selectedTransaction.amount).toFixed(2)}</Typography>
                                </Box>
                                {selectedTransaction.discountAmount && selectedTransaction.discountAmount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="success.main">
                                            Discount {selectedTransaction.discountPercentage > 0 && `(${selectedTransaction.discountPercentage}%)`}
                                        </Typography>
                                        <Typography variant="body2" color="success.main">
                                            -${Number(selectedTransaction.discountAmount).toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                                {selectedTransaction.tax && selectedTransaction.tax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Tax</Typography>
                                        <Typography variant="body2">${Number(selectedTransaction.tax).toFixed(2)}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                    <Typography variant="body1" fontWeight="bold">Total</Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        ${Number(selectedTransaction.totalAmount || selectedTransaction.amount).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTransactionDetailsOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Retry Payment Dialog */}
            <Dialog
                open={retryPaymentDialogOpen}
                onClose={() => !retryPaymentProcessing && handleCloseRetryPaymentDialog()}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {retryPaymentResults.length > 0 ? 'Retry Results' : 'Retry Failed Payments'}
                </DialogTitle>
                <DialogContent>
                    {!retryPaymentProcessing && retryPaymentResults.length === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body1" gutterBottom>
                                Your payment method has been updated successfully!
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                                You have <strong>{failedTransactionsCount}</strong> failed or pending payment{failedTransactionsCount !== 1 ? 's' : ''}.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Would you like to retry {failedTransactionsCount === 1 ? 'this payment' : 'these payments'} now with your new payment method?
                            </Typography>
                        </Box>
                    )}

                    {retryPaymentProcessing && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                            <CircularProgress size={48} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Processing payments...
                            </Typography>
                        </Box>
                    )}

                    {retryPaymentResults.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Results:
                            </Typography>
                            {retryPaymentResults.map((result, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Alert severity={result.success ? 'success' : 'error'}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {result.type} - ${Number(result.amount).toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {result.message}
                                            </Typography>
                                        </Box>
                                    </Alert>
                                </Box>
                            ))}
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="body2">
                                    <strong>Summary:</strong> {retryPaymentResults.filter(r => r.success).length} succeeded, {retryPaymentResults.filter(r => !r.success).length} failed
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {retryPaymentResults.length === 0 && !retryPaymentProcessing && (
                        <>
                            <Button onClick={handleCloseRetryPaymentDialog}>
                                No, I'll Do It Later
                            </Button>
                            <Button
                                onClick={handleRetryPayment}
                                color="primary"
                                variant="contained"
                            >
                                Yes, Retry Now
                            </Button>
                        </>
                    )}
                    {retryPaymentResults.length > 0 && (
                        <Button onClick={handleCloseRetryPaymentDialog} variant="contained">
                            Close
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Payment Method Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Payment Method
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this payment method? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => cardToDelete && handleDeleteCard(cardToDelete)} 
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for error notifications */}
            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={6000}
                onClose={() => setSnackbarMessage(null)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Container>
    );
};

export default BillingPage;